import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Download,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  FileDown,
  Loader2
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

// CSV Template headers
const CSV_HEADERS = [
  'first_name',
  'last_name', 
  'email',
  'username',
  'role',
  'department',
  'status'
];

// Generate CSV template
const generateCSVTemplate = () => {
  const headers = CSV_HEADERS.join(',');
  const sampleRows = [
    'John,Doe,john.doe@example.com,jdoe,Safety Manager,Operations,active',
    'Jane,Smith,jane.smith@example.com,jsmith,Training Coordinator,HR,active',
    'Mike,Johnson,mike.j@example.com,mjohnson,Site Supervisor,Construction,active',
    ',,,,,,',
    '# Notes:,,,,,,',
    '# - first_name and last_name are required,,,,,,',
    '# - email is required and must be unique,,,,,,',
    '# - username is optional (will be auto-generated from email if not provided),,,,,,',
    '# - role defaults to Employee if not provided,,,,,,',
    '# - department is optional,,,,,,',
    '# - status can be active or inactive (defaults to active),,,,,,',
  ];
  
  return headers + '\n' + sampleRows.join('\n');
};

// Parse CSV content
const parseCSV = (content) => {
  const lines = content.split('\n').filter(line => line.trim() && !line.startsWith('#'));
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  
  const data = [];
  const errors = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    // Skip empty lines or comments
    if (!line.trim() || line.startsWith('#')) continue;
    
    const values = line.split(',').map(v => v.trim());
    
    if (values.length !== headers.length) {
      errors.push(`Row ${i + 1}: Column count mismatch (expected ${headers.length}, got ${values.length})`);
      continue;
    }
    
    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    
    // Validate required fields
    if (!row.email) {
      errors.push(`Row ${i + 1}: Email is required`);
      continue;
    }
    
    if (!row.first_name || !row.last_name) {
      errors.push(`Row ${i + 1}: First name and last name are required`);
      continue;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(row.email)) {
      errors.push(`Row ${i + 1}: Invalid email format (${row.email})`);
      continue;
    }
    
    // Set defaults
    row.status = row.status || 'active';
    row.compliance_percentage = 0;
    
    data.push(row);
  }
  
  return { data, errors };
};

export function TeamImportDialog({ open, onOpenChange, projectId, onImportComplete }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv')) {
      setErrors(['Please select a CSV file']);
      return;
    }
    
    setFile(selectedFile);
    setErrors([]);
    setParsing(true);
    
    try {
      const content = await selectedFile.text();
      const { data, errors: parseErrors } = parseCSV(content);
      
      if (parseErrors.length > 0) {
        setErrors(parseErrors);
        setPreview(null);
      } else {
        setPreview(data);
        setErrors([]);
      }
    } catch (error) {
      setErrors(['Failed to read file: ' + error.message]);
      setPreview(null);
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!preview || preview.length === 0) return;
    
    setImporting(true);
    setImportProgress(0);
    const results = { success: 0, failed: 0, errors: [] };
    
    try {
      for (let i = 0; i < preview.length; i++) {
        const member = preview[i];
        setImportProgress(Math.round((i / preview.length) * 100));
        
        try {
          // After migration: Create/find user_profile, then create space_member

          // 1. Check if user_profile exists by email
          let { data: userProfile, error: profileFetchError } = await supabase
            .from('user_profiles')
            .select('id, company_id')
            .eq('email', member.email)
            .maybeSingle();

          if (profileFetchError) {
            results.errors.push(`${member.email}: Error checking user profile: ${profileFetchError.message}`);
            results.failed++;
            continue;
          }

          // 2. Get project's company_id
          const { data: project, error: projectError } = await supabase
            .from('projects')
            .select('company_id')
            .eq('id', projectId)
            .single();

          if (projectError || !project) {
            results.errors.push(`${member.email}: Could not find project`);
            results.failed++;
            continue;
          }

          // 3. Create user_profile if doesn't exist
          if (!userProfile) {
            const { data: newProfile, error: createProfileError } = await supabase
              .from('user_profiles')
              .insert({
                first_name: member.first_name,
                last_name: member.last_name,
                email: member.email,
                job_title: member.role || 'Employee',
                company_id: project.company_id,
                auth_id: null, // Invited but not registered
                created_date: new Date().toISOString()
              })
              .select('id')
              .single();

            if (createProfileError) {
              if (createProfileError.code === '23505') {
                results.errors.push(`${member.email}: Email already exists`);
              } else {
                results.errors.push(`${member.email}: ${createProfileError.message}`);
              }
              results.failed++;
              continue;
            }

            userProfile = newProfile;

            // 4. Create company_member for new user
            const { error: companyMemberError } = await supabase
              .from('company_members')
              .insert({
                company_id: project.company_id,
                user_id: userProfile.id,
                role: 'member',
                created_date: new Date().toISOString()
              });

            if (companyMemberError && companyMemberError.code !== '23505') {
              // Ignore duplicate errors, log others
              console.warn('Company member creation warning:', companyMemberError);
            }
          }

          // 5. Check if space_member already exists
          const { data: existingSpaceMember } = await supabase
            .from('space_members')
            .select('user_id')
            .eq('user_id', userProfile.id)
            .eq('project_id', projectId)
            .maybeSingle();

          if (existingSpaceMember) {
            results.errors.push(`${member.email}: Already a member of this project`);
            results.failed++;
            continue;
          }

          // 6. Create space_member
          const { data: spaceMember, error: spaceMemberError } = await supabase
            .from('space_members')
            .insert({
              user_id: userProfile.id,
              project_id: projectId,
              department: member.department || null,
              status: member.status || 'active',
              show_on_matrix: true,
              created_date: new Date().toISOString()
            })
            .select()
            .single();

          if (spaceMemberError) {
            results.errors.push(`${member.email}: ${spaceMemberError.message}`);
            results.failed++;
          } else {
            results.success++;
            if (onImportComplete) {
              onImportComplete(spaceMember);
            }
          }
        } catch (err) {
          results.errors.push(`${member.email}: ${err.message}`);
          results.failed++;
        }
      }
      
      setImportProgress(100);
      setImportResults(results);
    } catch (error) {
      setErrors(['Import failed: ' + error.message]);
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = generateCSVTemplate();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'team_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setErrors([]);
    setImportResults(null);
    setImportProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[90vw] max-h-[90vh] overflow-hidden p-0">
        <div className="overflow-y-auto max-h-[90vh] p-6">
        <DialogHeader>
          <DialogTitle>Import Team Members</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import team members in bulk
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Download Template Section */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileDown className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Download CSV Template</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Start with our template that includes all required fields and sample data
                  </p>
                  <Button 
                    onClick={handleDownloadTemplate}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upload Section */}
          {!importResults && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Upload CSV File</h3>
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      {!file ? (
                        <div>
                          <Upload className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                          <p className="text-sm text-muted-foreground mb-2">
                            Drag and drop your CSV file here, or
                          </p>
                          <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={parsing}
                          >
                            Browse Files
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="h-8 w-8 text-blue-600" />
                            <div className="text-left">
                              <p className="font-medium">{file.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {(file.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleReset}
                          >
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Errors */}
                  {errors.length > 0 && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-1">
                          <p className="font-medium">Validation Errors:</p>
                          <ul className="list-disc list-inside text-sm">
                            {errors.slice(0, 5).map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                            {errors.length > 5 && (
                              <li>... and {errors.length - 5} more errors</li>
                            )}
                          </ul>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Preview */}
                  {preview && preview.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold">Preview</h3>
                        <Badge variant="secondary">
                          {preview.length} members to import
                        </Badge>
                      </div>
                      <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-white z-10">
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Role</TableHead>
                              <TableHead>Department</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {preview.slice(0, 10).map((member, index) => (
                              <TableRow key={index}>
                                <TableCell>{member.first_name} {member.last_name}</TableCell>
                                <TableCell>{member.email}</TableCell>
                                <TableCell>{member.role || 'Team Member'}</TableCell>
                                <TableCell>{member.department || 'General'}</TableCell>
                                <TableCell>
                                  <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                                    {member.status || 'active'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                            {preview.length > 10 && (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground">
                                  ... and {preview.length - 10} more members
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* Import Progress */}
                  {importing && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Importing...</span>
                        <span className="text-sm text-muted-foreground">{importProgress}%</span>
                      </div>
                      <Progress value={importProgress} />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Import Results */}
          {importResults && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <h3 className="font-semibold">Import Complete</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <div>
                        <p className="text-2xl font-bold">{importResults.success}</p>
                        <p className="text-sm text-muted-foreground">Successfully imported</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <div>
                        <p className="text-2xl font-bold">{importResults.failed}</p>
                        <p className="text-sm text-muted-foreground">Failed to import</p>
                      </div>
                    </div>
                  </div>

                  {importResults.errors.length > 0 && (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-1">
                          <p className="font-medium">Import errors:</p>
                          <ul className="list-disc list-inside text-sm">
                            {importResults.errors.slice(0, 5).map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                            {importResults.errors.length > 5 && (
                              <li>... and {importResults.errors.length - 5} more errors</li>
                            )}
                          </ul>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {importResults ? 'Close' : 'Cancel'}
            </Button>
            {!importResults && preview && preview.length > 0 && (
              <Button 
                onClick={handleImport} 
                disabled={importing}
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import {preview.length} Members
                  </>
                )}
              </Button>
            )}
            {importResults && (
              <Button onClick={handleReset}>
                Import More
              </Button>
            )}
          </div>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export team members to CSV
export const exportTeamToCSV = (teamMembers, spaceName) => {
  // Create CSV headers
  const headers = [
    'Name',
    'Email',
    'Username',
    'Role',
    'Department',
    'Status',
    'Compliance %',
    'Sites',
    'Last Active'
  ];
  
  // Create CSV rows
  const rows = teamMembers.map(member => [
    member.name || '',
    member.email || '',
    member.username || '',
    member.role || '',
    member.department || '',
    member.status || '',
    member.compliance || member.compliance_percentage || '0',
    member.sites || '1',
    member.lastActive || member.last_active || ''
  ]);
  
  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => {
      // Escape commas and quotes in cell values
      const value = String(cell);
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(','))
  ].join('\n');
  
  // Create and download the file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${spaceName || 'team'}_members_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  window.URL.revokeObjectURL(url);
};